import { AxiosError } from 'axios';
import { ChangeEvent } from 'react';
import { AnyAction, Dispatch } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { handleErrorToast } from 'src/components/molecules';
import { DateFormats, formatDate, isValidDate } from 'src/utils/time';
import {
  PUBLIC_LOGIN,
  PUBLIC_MESSAGES,
  PUBLIC_REPORTS,
  PUBLIC_TOUCH,
  VAULT_TALK_LOGIN,
  handleAxiosError,
} from '../api';
import axios from '../axios';
import * as Types from '../types';
import { closeSocket, createSocket } from './socket';

export const clearSession = () => async (dispatch: Dispatch<AnyAction>) => {
  dispatch({ type: Types.OpenReportTypes.RESET });
  dispatch({ type: Types.OpenReportTypes.PHONENUMBER_RESET });
  closeSocket();
};

export const getSocket = (token: string) => {
  return createSocket(token, clearSession);
};

export const connectSocket = (token: string, dispatch: Dispatch<AnyAction>) => {
  const socket = createSocket(token, clearSession);

  socket.emit('join', { token });

  socket.on('chat', (message: Types.Message) => {
    dispatch({ type: Types.OpenReportTypes.MESSAGE, payload: message });
  });
  socket.on('chat-read-messages', (messages: Types.Message[]) => {
    messages.forEach((message) =>
      dispatch({ type: Types.OpenReportTypes.READ_MESSAGE, payload: message }),
    );
  });
};

const publicHydrate =
  (session: Types.Session) =>
  async (dispatch: ThunkDispatch<Types.State, void, AnyAction>) => {
    connectSocket(session.token, dispatch);

    dispatch({ type: Types.OpenReportTypes.SESSION, payload: session });

    dispatch({
      type: Types.OrganisationTypes.FETCH,
      payload: session.account.user.organisation,
    });

    await dispatch(fetchPublicReports());
  };

export const createPublicSession =
  (username: string, password: string, slug: string) =>
  async (dispatch: ThunkDispatch<Types.State, void, AnyAction>) => {
    try {
      const {
        data: { data: session },
      } = await axios.post(PUBLIC_LOGIN, {
        username,
        password,
        slug,
      });

      return await dispatch(publicHydrate(session));
    } catch (error) {
      throw handleAxiosError(
        error as AxiosError<{ errors: { message: string }[] }>,
      );
    }
  };

export const createVaultTalkPublicSession =
  (phoneNumber: string, pin: string, slug: string) =>
  async (dispatch: ThunkDispatch<Types.State, void, AnyAction>) => {
    try {
      const {
        data: { data: session },
      } = await axios.post(VAULT_TALK_LOGIN, {
        phoneNumber,
        pin,
        slug,
      });

      return await dispatch(publicHydrate(session));
    } catch (error) {
      throw handleAxiosError(
        error as AxiosError<{ errors: { message: string }[] }>,
      );
    }
  };

export const fetchPublicSession =
  () => async (dispatch: ThunkDispatch<Types.State, void, AnyAction>) => {
    dispatch({
      type: Types.FetchingType.FETCHING,
      payload: true,
    });

    try {
      const {
        data: { data: session },
      } = await axios.get(PUBLIC_LOGIN);

      await dispatch(publicHydrate(session));
    } catch (error) {
      handleAxiosError(error as AxiosError<{ errors: { message: string }[] }>);
    } finally {
      dispatch({
        type: Types.FetchingType.FETCHING_COMPLETED,
        payload: false,
      });
    }
  };

export const touch = () => () => axios.post(PUBLIC_TOUCH);

export const getExpiry = () => async () => {
  const response = await axios({
    method: 'get',
    url: PUBLIC_TOUCH,
  });

  return response?.data?.data?.updatedAt;
};

export const fetchPublicReports =
  () =>
  async (
    dispatch: Dispatch<AnyAction>,
    getState: () => Types.State,
  ): Promise<void> => {
    try {
      const publicReportsResult = await axios.get(PUBLIC_REPORTS);

      if (!publicReportsResult || publicReportsResult.data.status !== 200) {
        throw new Error('Failed to fetch public reports');
      }

      const publicReports: Types.Report[] =
        publicReportsResult.data.publicReports.map(
          (element: {
            id: string;
            createdAt: string;
            status: string;
            category: string;
            reportedCategory: string;
            determinedCategory: string;
            assignee: { name: string };
            anonymous: boolean;
            reporter: string;
            source: string;
          }) => ({
            reportId: element.id,
            date: isValidDate(element.createdAt)
              ? formatDate(
                  element.createdAt,
                  DateFormats.DD_MMM_YYYY,
                ).toLocaleUpperCase()
              : '',
            status: element.status,
            type: element.category,
            reportedCategory: element.reportedCategory,
            determinedCategory: element.determinedCategory,
            assignee: element.assignee ? element.assignee.name : '',
            reporter: element.anonymous ? 'Anonymous' : element.reporter,
            source: element.source,
          }),
        );

      dispatch({
        payload: publicReports,
        type: Types.OpenReportTypes.FETCH_ALL_PUBLIC,
      });

      for (const { reportId } of publicReports) {
        await fetchPublicMessages(reportId)(dispatch, getState);
      }
      return;
    } catch (error) {
      console.log('**ERROR: ', error);
      handleErrorToast({ toastMessage: 'Fetching reports failed.' });
      throw error;
    }
  };

export const fetchPublicMessages =
  (report: string) =>
  async (dispatch: Dispatch<AnyAction>, getState: () => Types.State) => {
    try {
      const translateMessages =
        getState().openReporting.openReportingReducer?.translateMessages;

      const response = await axios.get(
        PUBLIC_MESSAGES(report, translateMessages),
      );
      dispatch({
        // Starting the process of seperating the openReporting reducer
        type: Types.OpenReportTypes.MESSAGES,
        payload: { reportId: report, messages: response.data.data },
      });
      return;
    } catch (error) {
      dispatch({
        // Starting the process of seperating the openReporting reducer
        type: Types.OpenReportTypes.MESSAGES,
        payload: { reportId: report, messages: [] },
      });
      console.log('ERROR: ', error);
      throw error;
    }
  };

export const clearPublicReportData = () => ({
  type: Types.OpenReportTypes.CLEAR,
});

export const setOpenReportQuestion = (
  type: Types.OpenReportTypes,
  text: string,
) => ({
  type,
  payload: text,
});

export const selectSubmissionType = (selected: Types.CategorisedType) => ({
  type: Types.OpenReportTypes.SELECT_SUBMISSION_TYPE,
  payload: selected,
});

export const selectRelationshipType = (selected: Types.CategorisedType) => ({
  type: Types.OpenReportTypes.SELECT_RELATIONSHIP_TYPE,
  payload: selected,
});

export const setEventDate = (date: string) => {
  return {
    type: Types.OpenReportTypes.SET_EVENT_DATE,
    payload: date,
  };
};

export const setEventTime = (time: string) => {
  return {
    type: Types.OpenReportTypes.SET_EVENT_TIME,
    payload: time,
  };
};

export const setSelectedCountry = (countryName: string) => ({
  type: Types.OpenReportTypes.SET_SELECTED_COUNTRY,
  payload: countryName,
});

export const setOrganisationCountries = (
  countries: Array<{ name: string }>,
) => ({
  type: Types.OpenReportTypes.SET_ORGANISATION_COUNTRIES,
  payload: countries,
});

export const setSelectedSite = (siteName: string) => ({
  type: Types.OpenReportTypes.SET_SELECTED_SITE,
  payload: siteName,
});

export const setOrganisationSites = (sites: Array<{ name: string }>) => ({
  type: Types.OpenReportTypes.SET_ORGANISATION_SITES,
  payload: sites,
});

export const setEmail = (
  event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
) => ({
  type: Types.OpenReportTypes.SET_EMAIL,
  payload: event,
});

export const setName = (
  event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
) => ({
  type: Types.OpenReportTypes.SET_NAME,
  payload: event,
});

export const setPassword = (
  event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
) => ({
  type: Types.OpenReportTypes.SET_PASSWORD,
  payload: event,
});

export const setConfirmation = (
  event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
) => ({
  type: Types.OpenReportTypes.SET_CONFIRMATION,
  payload: event,
});

export const setCurrentPassword = (
  event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
) => ({
  type: Types.OpenReportTypes.SET_CURRENT_PASSWORD,
  payload: event,
});

export const setNewPassword = (
  event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
) => ({
  type: Types.OpenReportTypes.SET_NEW_PASSWORD,
  payload: event,
});

export const setConfirmNewPassword =
  (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
  async (dispatch: Dispatch<AnyAction>) => {
    return dispatch({
      type: Types.OpenReportTypes.SET_CONFIRM_NEW_PASSWORD,
      payload: event,
    });
  };

export const setLoginUsername = (
  event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
) => ({
  type: Types.OpenReportTypes.SET_LOGIN_USERNAME,
  payload: event,
});

export const setLoginPassword = (
  event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
) => ({
  type: Types.OpenReportTypes.SET_LOGIN_PASSWORD,
  payload: event,
});

export const setLoginPhoneNumber = (
  event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
) => ({
  type: Types.OpenReportTypes.SET_LOGIN_PHONENUMBER,
  payload: event,
});

export const setLoginPin = (
  event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
) => ({
  type: Types.OpenReportTypes.SET_LOGIN_PIN,
  payload: event,
});

export const setNotFound = (payload: boolean) => ({
  type: Types.OpenReportTypes.SET_NOTFOUND,
  payload,
});

export const setLoading = (payload: boolean) => ({
  type: Types.OpenReportTypes.SET_LOADING,
  payload,
});

export const setNavMenuSize = (payload: boolean) => ({
  type: Types.OpenReportTypes.SET_NAV_MENU_SIZE,
  payload,
});

export const setOrganisation = (slug: string) => ({
  type: Types.OpenReportTypes.SET_ORGANISATION,
  payload: slug,
});

export const setOrganisationLogo = (logo: string) => ({
  type: Types.OpenReportTypes.SET_ORGANISATION_LOGO,
  payload: logo,
});

export const setOrganisationPrivacyStatement = (url?: string | null) => ({
  type: Types.OpenReportTypes.SET_ORGANISATION_PRIVACY_STATEMENT,
  payload: url,
});

export const onSubmit = (payload: {
  publicReporterAccount: Types.PublicReportingAccount | null;
  redirect?: string;
  loginValues: { username: string };
}) => ({
  type: Types.OpenReportTypes.ON_SUBMIT,
  payload,
});

export const setIsSubmitting = (isSubmitting: boolean) => ({
  type: Types.OpenReportTypes.SET_IS_SUBMITTING,
  payload: isSubmitting,
});

export const setPostSubmissionIsSubmitting = (
  isPostFileUploadSubmitting: boolean,
) => ({
  type: Types.OpenReportTypes.SET_POST_SUBMISSION_IS_SUBMITTING,
  payload: isPostFileUploadSubmitting,
});

export const setReportJustSubmitted = (reportJustSubmitted: boolean) => ({
  type: Types.OpenReportTypes.SET_REPORT_JUST_SUBMITTED,
  payload: reportJustSubmitted,
});

export const setUploadErrors = (fileUploadErrors: number) => ({
  type: Types.OpenReportTypes.SET_UPLOAD_ERRORS,
  payload: fileUploadErrors,
});

export const setPostSubmissionUploadErrors = (
  postFileUploadErrors: number,
) => ({
  type: Types.OpenReportTypes.SET_POST_SUBMISSION_UPLOAD_ERRORS,
  payload: postFileUploadErrors,
});

export const onChangePassword = () => ({
  type: Types.OpenReportTypes.ON_CHANGE_PASSWORD,
});

export const setFetchedChallenge = (payload: {
  reference: string;
  challenge: string;
}) => ({
  type: Types.OpenReportTypes.SET_FETCHED_CHALLENGE,
  payload,
});

export const setSolutionToken = (payload: string) => ({
  type: Types.OpenReportTypes.SET_SOLUTION_TOKEN,
  payload,
});

export const setUserAnswer = (payload: string) => ({
  type: Types.OpenReportTypes.SET_USER_ANSWER,
  payload,
});
